// DashboardScreen - §4 개발품 종합현황.
//
// **이 화면 하나가 §20 의 7개 질문 중 6개에 클릭 0회로 답해야 한다.**
// 그래서 세 가지를 지킨다.
//   1. 데이터 호출은 2개다 (`/dashboard/summary`, `/dashboard/items`).
//      행마다 마일스톤을 따로 읽는 N+1 은 없다 (REQ-GOAL-011).
//   2. 필터를 localStorage 에 저장하지 않는다 (결정 S-6). 지난번 필터가
//      남아 지연 건이 숨으면 §20 목표가 깨진다.
//   3. 지연 0 건이면 화면이 조용해진다. `KpiCard` 가 스스로 0 붕괴하고,
//      알림 배너는 값이 0 인 카드를 렌더하지 않으며, 표의 지연 그룹 헤더도
//      생기지 않는다. 여기서 별도 분기를 추가하지 않는다 (디자인시스템 §10-17).
import { DevItemTable } from "@/components/DevItemTable";
import { KpiCard, KpiHeader } from "@/components/KpiCard";
import { WeekRibbon } from "@/components/WeekRibbon";
import { StatusDot } from "@/components/StatusIndicator";
import {
  Button,
  Checkbox,
  Chip,
  DataBoundary,
  EmptyState,
  FilterBar,
  Icon,
  Select,
  Skeleton,
  TableSkeleton,
  TextInput,
} from "@/components/ui";
import {
  type AlertCard,
  type Customer,
  type DashboardSort,
  type Staff,
  listProductionPlans,
} from "@/lib/api";
import { addDays, asOfLabel, isoDate, parseIsoDate } from "@/lib/dates";
import { useDashboard, useRibbon } from "@/lib/hooks";
import {
  DEFAULT_SETTINGS,
  MILESTONE_DEFS,
  STATUS_CODES,
  type StatusCode,
  formatDDay,
  statusKey,
} from "@/lib/status";
import type { ScreenNav } from "@/screens/nav";
import { useState } from "react";

interface Filters {
  q: string;
  customerId: string;
  staffId: string;
  stage: string;
  statuses: StatusCode[];
  includeArchived: boolean;
  /** SOP 임박 행만. 서버가 걸러야 `limit` 뒤에서 행이 조용히 사라지지 않는다. */
  sopOnly: boolean;
  /**
   * "생산예정이나 원소재/금형 미입고" 알림에서 온 개발품 id 목록.
   * 그 카운터는 이슈 행이 아니라 생산계획을 세므로, 이슈 화면으로 보내면
   * 클릭한 것이 없는 목록에 착지한다. 대신 §4-2 표를 해당 개발품으로 좁힌다.
   */
  prepWarnedIds: readonly string[];
}

/** 리본이 덮는 주 수. 8주 = 두 달 - SOP 리드타임의 실무 시야다. */
const RIBBON_WEEKS = 8;

const EMPTY_FILTERS: Filters = {
  q: "",
  customerId: "",
  staffId: "",
  stage: "",
  statuses: [],
  includeArchived: false,
  sopOnly: false,
  prepWarnedIds: [],
};

const activeCount = (f: Filters): number =>
  (f.q ? 1 : 0) +
  (f.customerId ? 1 : 0) +
  (f.staffId ? 1 : 0) +
  (f.stage ? 1 : 0) +
  (f.statuses.length > 0 ? 1 : 0) +
  (f.includeArchived ? 1 : 0) +
  (f.sopOnly ? 1 : 0) +
  (f.prepWarnedIds.length > 0 ? 1 : 0);

export const DashboardScreen = ({
  nav,
  customers,
  staff,
}: {
  nav: ScreenNav;
  customers: readonly Customer[];
  staff: readonly Staff[];
}) => {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [sort, setSort] = useState<DashboardSort>("default");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [alertBusy, setAlertBusy] = useState<AlertCard["code"] | null>(null);

  const { summary, items } = useDashboard(
    {
      q: filters.q,
      customerId: filters.customerId,
      staffId: filters.staffId,
      stage: filters.stage,
      status: filters.statuses.join(","),
      includeArchived: filters.includeArchived,
      sopImminent: filters.sopOnly,
    },
    sort,
    order
  );

  // ponytail: 미입고 필터만 클라이언트 사이드다. 서버에 id 목록 파라미터가
  // 없고 대상은 생산 조회기간(기본 D+7) 안의 생산계획으로 한정되어 작다.
  // 서버가 `?dev_item_ids=` 를 열면 그때 넘긴다.
  // 리본은 summary 의 asOf 를 기준으로 8주를 본다. asOf 가 오기 전에는
  // 빈 문자열이라 훅이 호출을 건너뛴다 - 오늘 날짜를 클라이언트가
  // 지어내면 서버의 `?today=` 주입과 어긋난다.
  const ribbon = useRibbon(summary.data?.asOf ?? "", RIBBON_WEEKS);

  const rows =
    filters.prepWarnedIds.length > 0
      ? items.data.items.filter((i) => filters.prepWarnedIds.includes(i.id))
      : items.data.items;
  const filtered = activeCount(filters) > 0;
  const kpi = summary.data?.kpi;

  // 지연 카드가 숫자만 말하면 "그래서 어느 건?"이 남는다. 표를 스캔하지
  // 않고 답하도록 D-DAY 가 가장 급한 지연 건 하나를 힌트로 붙인다.
  // 표에 없는 값을 만들지 않는다 - 이미 받아온 행에서만 고른다.
  const soonestLate = rows
    .filter((r) => r.status === "red" && r.dDay !== null)
    .sort((a, b) => (a.dDay ?? 0) - (b.dDay ?? 0))[0];
  const soonestLateHint = soonestLate
    ? `${soonestLate.partName} ${formatDDay(soonestLate.dDay)}`
    : undefined;
  const alerts = summary.data?.alerts ?? [];
  const mastersMissing = customers.length === 0 || staff.length === 0;

  const set = <K extends keyof Filters>(k: K, v: Filters[K]) =>
    setFilters((f) => ({ ...f, [k]: v }));

  const toggleStatus = (code: StatusCode) =>
    setFilters((f) => ({
      ...f,
      sopOnly: false,
      statuses: f.statuses.includes(code)
        ? f.statuses.filter((s) => s !== code)
        : [...f.statuses, code],
    }));

  const onlyStatus = (code: StatusCode) =>
    setFilters((f) => ({ ...f, sopOnly: false, statuses: [code] }));

  const onSort = (col: DashboardSort) => {
    if (sort === col) {
      setOrder(order === "asc" ? "desc" : "asc");
      return;
    }
    setSort(col);
    setOrder(col === "progress" || col === "status" ? "desc" : "asc");
  };

  const reset = () => {
    setFilters(EMPTY_FILTERS);
    setSort("default");
    setOrder("asc");
  };

  return (
    <div className="flex flex-col gap-s5">
      <header className="flex flex-wrap items-baseline justify-between gap-s3">
        <p
          className="text-caption tabular-nums"
          data-num=""
          style={{ color: "var(--text-tertiary)" }}
        >
          {asOfLabel(summary.data?.asOf ?? "")}
        </p>
        <Button
          variant="primary"
          size="md"
          icon={<Icon name="plus" size={15} />}
          onClick={nav.openCreate}
        >
          개발품 등록
        </Button>
      </header>

      {/* §15 알림. 값이 0 인 카드는 아예 없고, 5개가 모두 0 이면 이 영역 자체가
          렌더되지 않는다 (REQ-ALERT-008). 빈 박스도 두지 않는다. */}
      {alerts.length > 0 && (
        <AlertBanner
          alerts={alerts}
          busy={alertBusy}
          onOpen={async (code) => {
            if (code === "sop_imminent") {
              setFilters({ ...EMPTY_FILTERS, sopOnly: true });
              return;
            }
            if (code === "this_week") {
              nav.goto("schedule");
              return;
            }
            if (code === "delayed") {
              nav.goto("issues");
              return;
            }
            // 미입고 2종: 경고가 붙은 생산계획을 읽어 그 개발품으로 표를 좁힌다.
            setAlertBusy(code);
            try {
              const ids = await prepWarnedItemIds(summary.data?.asOf ?? "");
              if (ids.length === 0) {
                nav.toast("경고 대상 생산계획을 찾지 못했어요. 새로고침해 주세요.", "error");
                return;
              }
              setFilters({ ...EMPTY_FILTERS, prepWarnedIds: ids });
            } catch (err) {
              nav.toast(err instanceof Error ? err.message : "불러오지 못했어요.", "error");
            } finally {
              setAlertBusy(null);
            }
          }}
        />
      )}

      {/* 시간축. KPI 는 "지금 몇 건", 리본은 "언제 몰려 있는가" 에 답한다.
          자기 자리에서만 로딩하므로 느려도 아래 표를 막지 않는다. */}
      <WeekRibbon
        cells={ribbon.data}
        loading={ribbon.loading}
        onSelectWeek={() => nav.goto("schedule")}
      />

      <DataBoundary
        loading={summary.loading}
        error={summary.error}
        onRetry={() => void summary.reload()}
        skeleton={
          <div className="flex flex-col gap-s3">
            <div className="grid grid-cols-1 gap-s3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="grid lg:col-span-2">
                <Skeleton h={104} />
              </div>
              <Skeleton h={104} />
              <Skeleton h={104} />
            </div>
            <KpiHeader>
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} h={64} />
              ))}
            </KpiHeader>
          </div>
        }
      >
        {kpi && (
          <div className="flex flex-col gap-s3">
            {/* 위계는 하나다. 지연이 44px 로 먼저 말하고, SOP 임박과 전체가
                그 옆에 서고, 나머지 5개는 아래 한 줄로 내려간다. 이전 판은
                8개가 같은 무게로 깔려서 값이 0 인 칸 4개가 제일 넓었다. */}
            <div className="grid grid-cols-1 gap-s3 sm:grid-cols-2 lg:grid-cols-4">
              {/* grid 로 감싸야 안의 카드가 2칸 폭을 다 쓴다 - inline 래퍼는
                  늘어나지 않아 히어로가 한 칸 크기로 쪼그라든다. */}
              <div className="grid lg:col-span-2">
                <KpiCard
                  label="지연"
                  value={kpi.red}
                  emphasis="alarm"
                  state="late"
                  hero
                  hint={soonestLateHint && `가장 급한 건 ${soonestLateHint}`}
                  active={filters.statuses.length === 1 && filters.statuses[0] === "red"}
                  onClick={() => onlyStatus("red")}
                />
              </div>
              <KpiCard
                label="SOP 임박"
                value={kpi.sopImminent}
                emphasis="attend"
                state="warn"
                active={filters.sopOnly}
                onClick={() => setFilters({ ...EMPTY_FILTERS, sopOnly: true })}
              />
              <KpiCard label="전체 개발품" value={kpi.total} emphasis="anchor" onClick={reset} />
            </div>

            {/* 나머지 5개. 정상+주의+지연+미착수+완료 == 전체 가 성립하도록
                전부 남긴다 (결정 D-11). 위계는 크기와 구분선이 낮춘다. */}
            <div className="pt-s3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
              <KpiHeader>
                <KpiCard
                  label="정상"
                  value={kpi.green}
                  emphasis="inform"
                  active={filters.statuses.length === 1 && filters.statuses[0] === "green"}
                  onClick={() => onlyStatus("green")}
                />
                <KpiCard
                  label="일정주의"
                  value={kpi.yellow}
                  emphasis="inform"
                  active={filters.statuses.length === 1 && filters.statuses[0] === "yellow"}
                  onClick={() => onlyStatus("yellow")}
                />
                <KpiCard
                  label="금주 생산"
                  value={kpi.thisWeekProduction}
                  emphasis="inform"
                  onClick={() => nav.goto("schedule")}
                />
                <KpiCard
                  label="미착수"
                  value={kpi.gray}
                  emphasis="inform"
                  active={filters.statuses.length === 1 && filters.statuses[0] === "gray"}
                  onClick={() => onlyStatus("gray")}
                />
                <KpiCard
                  label="완료"
                  value={kpi.blue}
                  emphasis="inform"
                  active={filters.statuses.length === 1 && filters.statuses[0] === "blue"}
                  onClick={() => onlyStatus("blue")}
                />
              </KpiHeader>
            </div>
          </div>
        )}
      </DataBoundary>

      <div className="overflow-hidden rounded-card" style={{ background: "var(--surface)" }}>
        <FilterBar
          right={
            <>
              {filters.prepWarnedIds.length > 0 && (
                <Chip active onClick={() => set("prepWarnedIds", [])}>
                  생산 준비 미확보 {filters.prepWarnedIds.length}건
                </Chip>
              )}
              {filtered && (
                <span
                  className="text-micro tabular-nums"
                  data-num=""
                  style={{ color: "var(--text-tertiary)" }}
                >
                  필터 {activeCount(filters)}개 적용
                </span>
              )}
              <Button variant="ghost" size="sm" onClick={reset} disabled={!filtered}>
                초기화
              </Button>
            </>
          }
        >
          <span className="w-[16rem]">
            <TextInput
              type="search"
              value={filters.q}
              onChange={(v) => set("q", v)}
              ariaLabel="품명 · 품번 · 프로젝트 검색"
              placeholder="품명 · 품번 · 프로젝트 검색"
            />
          </span>
          <span className="w-[9rem]">
            <Select
              value={filters.customerId}
              onChange={(v) => set("customerId", v)}
              options={customers
                .filter((c) => c.active)
                .map((c) => ({ value: c.id, label: c.name }))}
              ariaLabel="고객사로 조회"
              placeholder="고객사 전체"
            />
          </span>
          <span className="w-[9rem]">
            <Select
              value={filters.staffId}
              onChange={(v) => set("staffId", v)}
              options={staff.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name }))}
              ariaLabel="담당자로 조회"
              placeholder="담당자 전체"
            />
          </span>
          <span className="w-[10rem]">
            <Select
              value={filters.stage}
              onChange={(v) => set("stage", v)}
              options={MILESTONE_DEFS.map((m) => ({ value: m.key, label: m.label }))}
              ariaLabel="진행단계로 조회"
              placeholder="진행단계 전체"
            />
          </span>
          {STATUS_CODES.map((code) => (
            <Chip
              key={code}
              active={filters.statuses.includes(code)}
              onClick={() => toggleStatus(code)}
              icon={<StatusDot status={statusKey(code)} size="row" labelled />}
            >
              {statusLabelOf(code)}
            </Chip>
          ))}
          <Checkbox
            checked={filters.includeArchived}
            onChange={(v) => set("includeArchived", v)}
            label="종료 포함"
          />
        </FilterBar>

        <DataBoundary
          loading={items.loading}
          error={items.error}
          onRetry={() => void items.reload()}
          skeleton={<TableSkeleton rows={12} cols={13} />}
        >
          {rows.length === 0 ? (
            mastersMissing && !filtered ? (
              <EmptyState
                icon="db"
                title="먼저 마스터를 등록하세요"
                description="고객사와 담당자를 등록하면 개발품을 등록할 수 있어요."
                action={
                  <Button variant="primary" size="md" onClick={() => nav.goto("master")}>
                    마스터로 이동
                  </Button>
                }
              />
            ) : filtered ? (
              <EmptyState
                icon="search"
                title="조건에 맞는 개발품이 없어요"
                description="조건에 맞는 개발품이 없어요. 필터를 조정해 보세요."
                action={
                  <Button variant="secondary" size="md" onClick={reset}>
                    필터 초기화
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon="list"
                title="등록된 개발품이 없어요"
                description="아직 등록된 개발품이 없어요. 첫 개발품을 등록하면 여기에 진행 현황이 모여요."
                action={
                  <Button variant="primary" size="md" onClick={nav.openCreate}>
                    개발품 등록
                  </Button>
                }
              />
            )
          ) : (
            <>
              <DevItemTable
                items={rows}
                sort={sort}
                order={order}
                onSort={onSort}
                onOpenItem={nav.openItem}
              />
              <p
                className="px-s4 py-s3 text-micro tabular-nums"
                data-num=""
                style={{ color: "var(--text-tertiary)" }}
              >
                {items.data.total}건 중 {rows.length}건 표시
              </p>
            </>
          )}
        </DataBoundary>
      </div>
    </div>
  );
};

/**
 * "생산예정이나 미입고" 경고가 붙은 개발품 id.
 * 서버가 `production_plans[].prep_warning` 을 이미 채워 주므로 판정을 다시
 * 하지 않고 그 값을 읽는다. 원소재/금형 두 카운터가 같은 경고 필드를 공유해서
 * 두 카드가 같은 집합으로 착지한다 - 카운터를 나누려면 서버가 종류를 내려줘야 한다.
 */
async function prepWarnedItemIds(asOf: string): Promise<string[]> {
  if (!asOf) return [];
  // 조회 기간은 §0-3 기본값(D+7). 회사가 값을 바꿔도 판정 자체는 서버가 채운
  // `prep_warning` 이 하므로 창의 폭만 달라진다 - /settings 를 더 부르지 않는다.
  const to = isoDate(addDays(parseIsoDate(asOf), DEFAULT_SETTINGS.prodPrepLookaheadDays));
  const plans = await listProductionPlans({ from: asOf, to, isDone: false });
  return [...new Set(plans.filter((p) => p.prepWarning).map((p) => p.devItemId))];
}

const statusLabelOf = (code: StatusCode): string =>
  code === "green"
    ? "정상"
    : code === "yellow"
      ? "주의"
      : code === "red"
        ? "지연"
        : code === "gray"
          ? "미착수"
          : "완료";

/**
 * §15 알림 배너. 라벨과 건수는 서버가 준 것을 그대로 쓴다 - `SOP D-30` 의
 * 30 은 설정값에서 보간되므로 하드코딩하면 설정을 바꿔도 문구가 안 바뀐다
 * (REQ-ALERT-007).
 */
const AlertBanner = ({
  alerts,
  busy,
  onOpen,
}: {
  alerts: readonly AlertCard[];
  busy: AlertCard["code"] | null;
  onOpen: (code: AlertCard["code"]) => void | Promise<void>;
}) => (
  <section
    aria-label="확인이 필요한 알림"
    className="flex flex-wrap items-center gap-s4 rounded-card px-s5 py-s4"
    style={{ background: "var(--surface)", boxShadow: "var(--shadow-raised)" }}
  >
    {alerts.map((a) => (
      <button
        key={a.code}
        type="button"
        disabled={busy !== null}
        onClick={() => void onOpen(a.code)}
        className="inline-flex items-center gap-s2 rounded-control px-s2 py-s1 text-label font-semibold transition-colors duration-fast ease-standard hover:bg-overlay-hover active:bg-overlay-press"
        style={{ color: a.icon === "red" ? "var(--state-late)" : "var(--state-warn)" }}
      >
        <StatusDot status={a.icon === "red" ? "late" : "warn"} size="md" labelled />
        {a.label}
        <span className="tabular-nums" data-num="">
          {a.count}건
        </span>
      </button>
    ))}
  </section>
);
